import React, { useState, useCallback, useRef, useEffect, Fragment, forwardRef, useImperativeHandle } from 'react'
import { Button, CustomAnchor, InputText, NoteInfo, PopoverMenuContentItem, PopoverThreeDots } from '../../components'
import { copyToClipboard } from '../../utils';
import ChatItem from './chat-item';
import { normalizeChatText } from './utils';
import References from './references';
import { askDocsStreamAPI, getChatHistoryAPI, isAbortError } from '../../common/services';
import PageLoader from '../page-loader';
import { useScrollContext } from '../../common/contexts/scroll';
import { useToaster } from '../../common/contexts/toast';
import { SCROLL_REFERENCE_KEYS } from '../../constants';
import { organizationState } from '../../atoms';
import { useRecoilValue } from 'recoil';

const SHOW_FEEDBACK_ACTIONS = false;

const Chat = forwardRef(({
    id: pastChatID = null,
    type: chatType = "organization",
    size = "medium", // options: medium, large
    withReferences = false,
    headerContent,
    objectId,
    houseId,
    showToaster,
    onAskQuestion,
    onResetChat,
    onReferenceClick,
    onRefrenceChange,
    placeholder = "Ask a question...",
    onClickDelete,
    disabled,
    docFilters,
    onDocFiltersChange,
    filterChipsContent,
    onInputFocus,
    onInputChange,
    onChatHistoryChange,
    onAskError,
}, ref) => {

    // local states
    const [chatHistory, setChatHistory] = useState([]);
    const [currentQuestion, setCurrentQuestion] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [pageLoader, setPageLoader] = useState(false);
    const [references, setReferences] = useState([]);

    // global states
    const { id: orgId } = useRecoilValue(organizationState);

    //ref
    const inputRef = useRef(null);
    const chatListRef = useRef(null);
    const uniqueId = useRef(Date.now())
    const validateObjectIdRef = useRef()
    const streamAbortControllerRef = useRef(null)
    const onDocFiltersChangeRef = useRef(onDocFiltersChange)
    onDocFiltersChangeRef.current = onDocFiltersChange
    const { registerRef } = useScrollContext()
    
    // chatid state
    const getDefaultChatId = useCallback(() => `${houseId || orgId}_${uniqueId.current}`, [houseId, orgId])
    const [chatId, setChatId] = useState(getDefaultChatId())

    const finalChatIdToUse = pastChatID || chatId
    const toDisableChat = disabled || isLoading

    const resetChat = useCallback((resetChatOptions = {}) => { 

        setChatHistory([]);
        setCurrentQuestion('');
        setReferences([]);

        // this will reset the chatId
        setChatId(resetChatOptions?.objectId ? `${houseId}_${resetChatOptions?.objectId}_${uniqueId.current}`: getDefaultChatId())

        // cancelling the chat in case it resets and call is being done
        if (isLoading && streamAbortControllerRef.current) {
            streamAbortControllerRef.current.abort()
        }

        onResetChat && onResetChat(resetChatOptions)
    }, [onResetChat, getDefaultChatId, houseId, isLoading]);

    const handleNewChat = useCallback(() => {
        uniqueId.current = Date.now()
        resetChat({ resetPastHistory: true, objectId : objectId || null })
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [resetChat, objectId]);

    const handleInputChange = useCallback((value) => {
        setCurrentQuestion(value);
        onInputChange && onInputChange(value);
    }, [onInputChange]);

    const handleAskQuestion = useCallback(async (suggestedQuestion) => {
        let questionValue = suggestedQuestion ? suggestedQuestion.trim() : currentQuestion.trim();
        
        if (!questionValue || toDisableChat) {
            return
        }
        
        setIsLoading(true)

        const newQuestion = {
            id: Date.now().toString(),
            question: questionValue,
            response: '',
            isLiked: false,
            isDisliked: false,
            status: "loading"
        }

        // Add question to chat list
        setChatHistory(prev => [...(prev || []), newQuestion])
        setCurrentQuestion('')
        onInputChange && onInputChange('')
        onAskQuestion && onAskQuestion(questionValue)

        let askDocsAPIData = {
            question: questionValue,
            chatId: finalChatIdToUse,
            type: chatType
        }

        if (houseId) {
            askDocsAPIData.houseId = houseId
        }

        if (objectId) {
            askDocsAPIData.objectId = objectId?.toString()
        }

        if (docFilters) {
            askDocsAPIData.docFilters = docFilters
        }

        try {
            streamAbortControllerRef.current = new AbortController()

            // The answer arrives in parts (docs message, answer tokens,
            // compliance note); the `done` event carries the assembled answer.
            let docsMessage = ''
            let tokensText = ''
            let complianceText = ''
            let assembledAnswer = ''
            let streamError = null

            const renderStreamedAnswer = () => {
                const combined = [docsMessage, tokensText, complianceText].filter(Boolean).join('')
                if (!combined) return
                setChatHistory(prev => prev.map(chat => chat.id === newQuestion.id ? ({ ...chat, response: combined, status: "streaming" }) : chat))
            }

            await askDocsStreamAPI(askDocsAPIData, {
                signal: streamAbortControllerRef.current.signal,
                onEvent: ({ event, data }) => {
                    const payload = parseStreamEventData(data)

                    switch (event) {
                        case 'token': {
                            let content = ''
                            if (payload == null) content = data // plain-text frame
                            else if (typeof payload === 'string') content = payload
                            else content = payload.content ?? ''

                            if (!content) return
                            // Accumulate verbatim. Escaped newlines are normalized
                            // at display time, once the math has been lifted out of
                            // the text: doing it here would eat any LaTeX command
                            // starting with "n" (\neq, \nabla).
                            tokensText += content
                            renderStreamedAnswer()
                            break
                        }
                        case 'docs_result': {
                            if (payload?.message) {
                                docsMessage = payload.message
                                renderStreamedAnswer()
                            }
                            break
                        }
                        case 'compliance_done': {
                            if (payload?.compliance) {
                                complianceText = payload.compliance
                                renderStreamedAnswer()
                            }
                            break
                        }
                        case 'done': {
                            assembledAnswer = payload?.data?.answer || ''
                            break
                        }
                        case 'error': {
                            streamError = new Error(payload?.message || data || 'AI stream failed')
                            break
                        }
                        // progress updates shown while waiting for the first content
                        case 'tool_call':
                        case 'tool_result':
                        case 'thinking': {
                            if (payload?.message) {
                                setChatHistory(prev => prev.map(chat => (chat.id === newQuestion.id && chat.status === "loading") ? ({ ...chat, progressMessage: payload.message }) : chat))
                            }
                            break
                        }
                        default:
                            break
                    }
                }
            })

            if (streamError) {
                throw streamError
            }

            // The `done` frame carries the assembled answer WITH doc-reference
            // links; chat history stores the raw citation brackets without the
            // urls. So keep the `done` answer and use history only for the
            // highlight info (and as a text fallback if `done` never arrived).
            let finalAnswer = assembledAnswer || [docsMessage, tokensText, complianceText].filter(Boolean).join('')
            let responseReferences = null

            try {
                const chatHistoryResponse = await getChatHistoryAPI({ chatId: finalChatIdToUse })
                const lastBotMessage = (chatHistoryResponse?.data?.chat || []).filter(chat => chat.sender === "bot").pop()
                if (!assembledAnswer && lastBotMessage?.text) {
                    finalAnswer = lastBotMessage.text
                }
                responseReferences = lastBotMessage?.docHighlightInfo
            } catch (historyError) {
                console.log("Error while fetching streamed answer details", historyError);
            }

            if (withReferences && responseReferences) {
                setReferences(responseReferences);
            } else if (responseReferences && onRefrenceChange) {
                onRefrenceChange(responseReferences)
            }

            setChatHistory(prev => prev.map(chat => chat.id === newQuestion.id ? ({ ...chat, response: finalAnswer, status: "completed", streamed: true }) : chat))
        } catch (error) {
            if (!isAbortError(error)) {
                console.log("Error while asking question", error);
                setChatHistory(prev => prev.map(chat => chat.id === newQuestion.id ? { ...chat, status: "error" } : chat))
                onAskError && onAskError(error)
            }
        } finally {
            setIsLoading(false);
        }

    }, [currentQuestion, toDisableChat, onAskQuestion, onInputChange, finalChatIdToUse, chatType, houseId, objectId, withReferences, onRefrenceChange, docFilters, onAskError]);

    const handleKeyPress = useCallback((e) => {
        if (toDisableChat) return
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleAskQuestion();
        }
    }, [handleAskQuestion, toDisableChat]);

    const onRetryAskQuestion = useCallback((chatData) => {
        // remove old question
        setChatHistory(oldChats => oldChats.filter(chat => chat.id !== chatData.id))
        // ask question again
        handleAskQuestion(chatData.question)
    }, [handleAskQuestion])

    const handleUpdateChat = useCallback((toUpdateChatId, updates) => {
        setChatHistory(prev =>
            prev.map(chat => ((chat.id === toUpdateChatId) ? { ...chat, ...updates } : chat))
        );
    }, []);

    const { showToaster: showGlobalToaster } = useToaster()
    const lastChatData = chatHistory?.[chatHistory.length - 1]
    const handleCopyLastResponse = useCallback(() => {
        if (!lastChatData?.response) return
        copyToClipboard(`${lastChatData.question}\n\n${normalizeChatText(lastChatData.response)}`, () => {
            showGlobalToaster && showGlobalToaster("copy-message", "Copied successfully to clipboard", true)
        })
    }, [lastChatData, showGlobalToaster]);

    const handleLike = useCallback(() => {
        lastChatData && handleUpdateChat(lastChatData.id, { isLiked: !lastChatData.isLiked, isDisliked: false });
    }, [lastChatData, handleUpdateChat]);

    const handleDislike = useCallback(() => {
        lastChatData && handleUpdateChat(lastChatData.id, { isDisliked: !lastChatData.isDisliked, isLiked: false });
    }, [lastChatData, handleUpdateChat]);

    // this will load the past chat history
    const loadPastChatHistory = useCallback(async (toLoadPastChatId) => {
        try {

            setPageLoader(true)

            const chatHistoryResponse = await getChatHistoryAPI({ chatId: toLoadPastChatId })
            const allChats = chatHistoryResponse.data.chat;
            const historyDocFilters = chatHistoryResponse.data.docFilters;

            // Restore filters from past chat if available
            if (historyDocFilters && onDocFiltersChangeRef.current) {
                onDocFiltersChangeRef.current(historyDocFilters)
            }

            setChatHistory(formatChatHistory(allChats))
            const lastChatRefrences = (allChats || []).pop()?.docHighlightInfo || [];

            if (withReferences) {
                setReferences(lastChatRefrences)
            }

            if (onRefrenceChange) {
                onRefrenceChange(lastChatRefrences)
            }

            // scroll to last
            if (chatListRef.current) {
                chatListRef.current.scrollTop = chatListRef.current.scrollHeight;
            }
        } catch (error) {
            console.log("Error while fetching Chat History", error);
        } finally {
            setPageLoader(false)
        }
    }, [withReferences, onRefrenceChange])


    // this will open the chat when id is passed from outside
    useEffect(() => {
        if (pastChatID) {
            loadPastChatHistory(pastChatID)
        }
    }, [loadPastChatHistory, pastChatID])

    // to reset chat when element changes but when not clicked from refrence 
    useEffect(() => {
        const objectChanged = objectId !== validateObjectIdRef.current
        if (!objectChanged || pastChatID) return // never reset when opening past chat

        const isChatOpen = !!chatHistory?.length || isLoading;

        // reset chat id ONLY from main chat screen
        if (!isChatOpen) {
            setChatId(objectId ? `${houseId}_${objectId}_${uniqueId.current}` : getDefaultChatId())
        } 

        validateObjectIdRef.current = objectId

    }, [objectId, chatHistory.length, isLoading, pastChatID, resetChat, houseId, getDefaultChatId]);

    // reference click handler
    const handleChatClick = useCallback((event) => {
        const target = event.target.closest('.ref-link-chat');
        if (!target) return

        const dataset = target.dataset

        if (dataset.type === "doc") {
            const fileObj = { 
                Bucket: dataset.bucket, 
                Key: dataset.key ? decodeURIComponent(dataset.key) : ""
            };
            onReferenceClick({ 
                pdfFileName: dataset.file, 
                pdfPageNumber: dataset.page, 
                sheet: dataset.sheet || undefined,
                row: dataset.row || undefined,
                docId: dataset.docId || undefined,
                file: fileObj
            })
        } else if (dataset.type === "element") {
            onReferenceClick({ objectId: dataset.objectid })
        }
    }, [onReferenceClick])

     useEffect(() => {
        registerRef(SCROLL_REFERENCE_KEYS.chatList, chatListRef.current)
        return () => registerRef(SCROLL_REFERENCE_KEYS.chatList, null)
    }, [registerRef, chatHistory])

    useEffect(() => {
        onChatHistoryChange && onChatHistoryChange(chatHistory)
    }, [chatHistory, onChatHistoryChange])

    // stop an in-flight stream when the chat unmounts
    useEffect(() => {
        return () => {
            if (streamAbortControllerRef.current) {
                streamAbortControllerRef.current.abort()
            }
        }
    }, [])

    const onDelete = useCallback(() => {
        if (onClickDelete) {
            onClickDelete(resetChat, finalChatIdToUse)
        }
    }, [onClickDelete, resetChat, finalChatIdToUse])

    useImperativeHandle(ref, () => ({
        askQuestion: (question) => handleAskQuestion(question),
    }), [handleAskQuestion])

    // loader enabled when clicked on any past chat
    if (!chatHistory?.length && pageLoader) {
        return <PageLoader />;
    }

    return (
        <div className={`bimmatch-chat-container chat-size-${size} ${!!chatHistory?.length ? "chat-started" : ""}`} onClick={handleChatClick}>
                {(!!chatHistory?.length) && (
                    <div className='new-chat-container'>
                        <div className='header-actions'>
                                <div className='new-chat-action'>
                                    <PopoverMenuContentItem label="New chat" src="/images/icon/return.svg" onClick={handleNewChat} className="new-chat-button" disabled={isLoading} />
                                    {filterChipsContent && <div className='filter-chips-content'>{filterChipsContent}</div>}
                                    <PopoverThreeDots options={[{ id: "delete", src: "/images/icon/trash.svg", label: "Delete", onClick: onDelete }]} className={'chat-popover'}/>
                                </div>
                        </div>
                    </div>
                )}
                {!!chatHistory?.length && <hr className='separator' />}
                {(objectId && headerContent) && (
                    <>
                        <div className='chat-header'>
                            <div className='docs-chat-header-container'>
                                {headerContent && headerContent}
                            </div>
                        </div>
                        <hr className='separator chat-header-separator' />
                    </>
                )}
                {!!chatHistory?.length && (
                    <div className={`chat-list-wrapper`} ref={chatListRef}>
                        {(chatHistory || []).map(chatData => (
                            <Fragment key={chatData.id}>
                                <ChatItem
                                    key={chatData.id}
                                    chatData={chatData}
                                    onRetry={() => onRetryAskQuestion(chatData)}
                                    chatType={chatType}
                                />
                                <hr className='separator chat-item-separator' />
                            </Fragment>
                        ))}

                        <div className='chat-bottom-actions'>
                            <NoteInfo note='Bimmatch AI can make mistakes. Check important info.' withInfoIcon />
                            {(!isLoading && !!lastChatData?.response) && (
                                <div className='action-items'>
                                    {SHOW_FEEDBACK_ACTIONS && (
                                        <>
                                            <img
                                                src={lastChatData.isLiked ? "/images/icon/thumb-up-filled.svg" : "/images/icon/thumb-up.svg"}
                                                alt="like"
                                                className={`action-item ${lastChatData.isLiked ? 'active' : ''}`}
                                                onClick={handleLike}
                                            />
                                            <img
                                                src={lastChatData.isDisliked ? "/images/icon/thumb-down-filled.svg" : "/images/icon/thumb-down.svg"}
                                                alt="dislike"
                                                className={`action-item ${lastChatData.isDisliked ? 'active' : ''}`}
                                                onClick={handleDislike}
                                            />
                                        </>
                                    )}
                                    <img
                                        src='/images/icon/copy.svg'
                                        alt='copy'
                                        className='action-item'
                                        onClick={handleCopyLastResponse}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {!isLoading && (
                    <div className='ask-input-wrapper'>
                        <div className={`ask-fields-wrapper ${chatHistory?.length ? "with-chat" : ""}`}>
                            <InputText
                                ref={inputRef}
                                placeholder={placeholder}
                                hideLabel
                                value={currentQuestion}
                                onChange={handleInputChange}
                                onKeyPress={handleKeyPress}
                                onFocus={onInputFocus}
                                disabled={toDisableChat}
                            />
                            <Button
                                type="secondary"
                                onClick={() => handleAskQuestion(false)}
                                disable={toDisableChat || !currentQuestion.trim()}
                            >
                                <img src='/images/icon/ai-white.svg' alt="Brain" className='ask-icon' />
                                <p>Ask</p>
                            </Button>
                        </div>
                        {!!chatHistory?.length && (
                            <NoteInfo
                                note={<>You are within the context of previous questions. Create a <CustomAnchor onClick={handleNewChat}>new chat</CustomAnchor> for a new topic.</>}
                                withInfoIcon
                            />
                        )}
                    </div>
                )}
                {(withReferences && !!references?.length) && (
                    <div className={`references-wrapper ${isLoading ? "loading" : "loaded"}`}>
                        <References
                            references={references}
                            loading={isLoading}
                            showToaster={showToaster}
                            onReferenceClick={onReferenceClick}
                        />
                    </div>
                )}
        </div>
    )
})

Chat.displayName = 'Chat'


// Frame data is a JSON envelope (e.g. {"content":" valves"} for token events);
// returns null when it is not JSON so callers can fall back to the raw text.
function parseStreamEventData(data) {
    if (!data) return null
    try {
        return JSON.parse(data)
    } catch (parseError) {
        return null
    }
}

// NOte: This is a temporary solution to format as api response will change
function formatChatHistory(apiData) {
    let formattedChat = []

    apiData.forEach((chatData, chatIndex) => {
        if (chatData.sender === "user") {
            formattedChat.push({
                id: `question-${chatIndex}`,
                question: chatData.text,
                response: ''
            })
        } else {
            formattedChat[formattedChat.length - 1].response = chatData.text
        }
    });
    return formattedChat
}

export default Chat